import Button from '../../libs/button/button.js';
import { decorateIcons } from '../../scripts/aem.js';
import { div, domEl, li, p, span, ul } from '../../scripts/dom-builder.js';
import moveInstrumentation from '../../scripts/utils/editor-support-utils.js';
import getI18next from '../../scripts/utils/translations/get-translations.js';

const state = {
  animation: null,
  isClosing: false,
  isExpanding: false,
};

function hasWrapper(el) {
  return (
    !!el?.firstElementChild &&
    window.getComputedStyle(el.firstElementChild).display === 'block'
  );
}

function getPreviousElementSibling(previousElement, selector) {
  return previousElement && previousElement.classList.contains(selector)
    ? previousElement
    : null;
}

function animateDetails(details, startHeight, endHeight) {
  state.animation = details.animate(
    {
      height: [startHeight, endHeight],
    },
    {
      duration: 300,
      easing: 'ease',
    },
  );
}

function onAnimationFinish(details) {
  state.animation = null;
  state.isClosing = false;
  state.isExpanding = false;
  details.style.height = '';
}

function updateButtonLabel(block, i18next) {
  const button = block.querySelector('.accordion__expand-all');

  if (!button) {
    return;
  }

  const isFullyExpanded = [...block.querySelectorAll('.accordion-item')].every(
    (item) => item.open,
  );
  button.querySelector('span').textContent = isFullyExpanded
    ? i18next.t('Accordion.CollapseAll.Label', 'Collapse All')
    : i18next.t('Accordion.ExpandAll.Label', 'Expand All');
}

function shrinkDetails(details, summary, block, i18next, isExpandAll = false) {
  state.isClosing = true;
  details.open = false;

  const startHeight = `${details.offsetHeight}px`;
  const endHeight = `${summary.offsetHeight}px`;

  if (!isExpandAll) {
    state.animation?.cancel();
  }

  animateDetails(details, startHeight, endHeight);

  state.animation.onfinish = () => {
    onAnimationFinish(details);
    updateButtonLabel(block, i18next);
  };
  state.animation.oncancel = () => {
    state.isClosing = false;
  };
}

function expand(details, summary, content, block, i18next, isExpandAll) {
  state.isExpanding = true;
  const startHeight = `${details.offsetHeight}px`;
  const endHeight = `${summary.offsetHeight + content.offsetHeight}px`;

  if (!isExpandAll) {
    state.animation?.cancel();
  }

  animateDetails(details, startHeight, endHeight);

  state.animation.onfinish = () => {
    onAnimationFinish(details);
    updateButtonLabel(block, i18next);
  };
  state.animation.oncancel = () => {
    state.isExpanding = false;
  };
}

function openDetails(
  details,
  summary,
  content,
  block,
  i18next,
  isExpandAll = false,
) {
  details.style.height = `${details.offsetHeight}px`;
  details.open = true;
  window.requestAnimationFrame(() =>
    expand(details, summary, content, block, i18next, isExpandAll),
  );
}

function onClickSummary(e, details, summary, content, block, i18next) {
  e.preventDefault();
  const arrow = summary.querySelector('.accordion-arrow');
  if (state.isClosing || !details.open) {
    arrow.classList.remove('details-closed');
    openDetails(details, summary, content, block, i18next);
  } else if (state.isExpanding || details.open) {
    arrow.classList.add('details-closed');
    shrinkDetails(details, summary, block, i18next);
  }
}

/**
 * Get primary and secondary buttons that are direct siblings.
 * @param accordionItemBody {HTMLElement}
 * @returns {*[]} Array of objects containing primary and secondary button containers.
 */
function getButtonSiblings(accordionItemBody) {
  const buttonContainers =
    accordionItemBody.querySelectorAll('.button-container');
  const pairs = [];

  buttonContainers.forEach((container) => {
    if (container.querySelector('.primary')) {
      const nextContainer = container.nextElementSibling;
      if (
        nextContainer &&
        nextContainer.classList.contains('button-container') &&
        nextContainer.querySelector('.secondary')
      ) {
        pairs.push({
          primaryContainer: container,
          secondaryContainer: nextContainer,
        });
      }
    }
  });

  return pairs;
}

/**
 * Wrap primary and secondary buttons in a single wrapper element.
 * @param block {HTMLElement} The accordion block.
 */
function wrapPrimarySecondaryButtonSiblings(block) {
  block
    .querySelectorAll('.accordion-item-body')
    .forEach((accordionItemBody) => {
      const pairs = getButtonSiblings(accordionItemBody);
      pairs.forEach(({ primaryContainer, secondaryContainer }) => {
        const wrapper = div({ class: 'primary-secondary-wrapper' });

        // Replace the primary container with the wrapper
        primaryContainer.parentNode.replaceChild(wrapper, primaryContainer);

        // Append the primary and secondary containers to the wrapper
        wrapper.appendChild(primaryContainer);
        wrapper.appendChild(secondaryContainer);
      });
    });
}

/**
 * Wrap accordion items in a semantic list for WCAG 1.3.1.
 * @param block {HTMLElement} The accordion block.
 */
function wrapItemsInList(block) {
  const items = [...block.querySelectorAll(':scope > details.accordion-item')];
  if (!items.length) {
    return;
  }
  const list = ul(
    { class: 'accordion-list' },
    ...items.map((details) => li(details)),
  );
  block.append(list);
}

export default async function decorate(block) {
  const i18next = await getI18next();
  let expandAllButton;

  [...block.children].forEach((row) => {
    // decorate accordion item label
    const label = row.children[0];
    const summaryArrow = span({
      class: 'icon icon-slim-arrow-right accordion-arrow details-closed',
    });
    const summaryContents = [summaryArrow, ...label.childNodes];
    const summary = domEl(
      'summary',
      { class: 'accordion-item-label' },
      ...(!hasWrapper(label) ? [p(...summaryContents)] : summaryContents),
    );

    // decorate accordion item body
    const body = !hasWrapper(row.children[1])
      ? p(row.children[1])
      : row.children[1];
    body.className = 'accordion-item-body';

    // decorate accordion item
    const details = domEl(
      'details',
      { class: 'accordion-item' },
      summary,
      body,
    );
    moveInstrumentation(row, details);
    row.replaceWith(details);
    summary.addEventListener('click', (e) =>
      onClickSummary(e, details, summary, body, block, i18next),
    );
  });
  // Create Accordion Header
  let prependHeader = false;
  const headerDiv = div({ class: 'accordion-header' });
  const noHeading = block.classList.contains('no-heading');

  if (!noHeading) {
    const accordionHeaderContainer = getPreviousElementSibling(
      block.parentElement.previousElementSibling,
      'default-content-wrapper',
    );
    const headerEl = accordionHeaderContainer?.querySelector(
      'h2:last-child, h3:last-child',
    );
    if (headerEl) {
      headerEl.classList.add('header-text');
      headerDiv.append(headerEl);
      if (accordionHeaderContainer.children.length === 0) {
        accordionHeaderContainer.remove();
      }
      prependHeader = true;
    }
  }

  // Accordion bulk-toggle functionality
  const hasExpandAll = block.classList.contains('expand-all');
  if (hasExpandAll) {
    expandAllButton = new Button(
      i18next.t('Accordion.ExpandAll.Label', 'Expand All'),
      null,
      'tertiary',
      'large',
    ).render();
    const buttonElement = expandAllButton.firstChild;
    buttonElement.removeAttribute('aria-label');
    buttonElement.classList.add('accordion__expand-all');
    const expandAllStatus = span({
      'aria-live': 'polite',
      role: 'status',
      class: 'hidden-screenreader-safe',
    });
    headerDiv.append(expandAllButton, expandAllStatus);
    prependHeader = true;

    expandAllButton.addEventListener('click', () => {
      const buttonLabel = expandAllButton.querySelector('span').textContent;
      const isExpanded =
        buttonLabel ===
          i18next.t('Accordion.CollapseAll.Label', 'Collapse All') ||
        buttonLabel === 'Collapse All';

      block.querySelectorAll('details').forEach((details) => {
        const summary = details.querySelector('.accordion-item-label');
        const arrow = summary.querySelector('.accordion-arrow');
        const content = details.querySelector('.accordion-item-body');

        if (!isExpanded) {
          arrow.classList.remove('details-closed');
          openDetails(details, summary, content, block, i18next, true);
        } else {
          arrow.classList.add('details-closed');
          shrinkDetails(details, summary, block, i18next, true);
        }
      });

      expandAllStatus.textContent = isExpanded
        ? i18next.t(
            'Accordion.AllSectionsCollapsed.Label',
            'All sections collapsed',
          )
        : i18next.t(
            'Accordion.AllSectionsExpanded.Label',
            'All sections expanded',
          );
    });
  }

  if (prependHeader) {
    block.prepend(headerDiv);
  }
  decorateIcons(block);
  wrapPrimarySecondaryButtonSiblings(block);
  wrapItemsInList(block);
}
